import { EMPLOYMENT_BONDS } from '@hue/contracts';
import {
  Button,
  IconButton,
  Input,
  Modal,
  MultiSelect,
  Select,
  SelectAllToggle,
  StatusPill,
  TimePicker,
  useIsWideViewport,
  useSnackbar,
} from '@hue/design-system';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, DoorOpen, PencilLine, Plus, RotateCcw, UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { choiceChips } from '../../app/choice-filters.js';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  LoadingCard,
  MultiFilter,
  RequestAlert,
  SortBar,
  SortableColumn,
} from '../../app/data-view.js';
import { useCollectionView } from '../../app/use-collection-view.js';
import {
  getApiError,
  schedulingApi,
  servicesApi,
  type SchedulingResource,
  type SchedulingResourceKind,
  type ServiceOffering,
} from '../../lib/api.js';
import { formatDateTime } from '../../lib/time-zone.js';
import { minuteLabel } from './calendar-model.js';
import { CommissionProfileFields } from './commission-profile-fields.js';
import { detailLabel } from './resource-labels.js';
import { ProfessionalAdvances } from './professional-advances.js';
import { ProfessionalServiceTermsFields } from './professional-service-terms.js';
import { useSchedulingUnits } from './scheduling-units.js';
import { DialogActions, FieldError } from './shared.js';

const activityOptions: Array<{ value: 'ACTIVE' | 'INACTIVE'; label: string }> = [
  { value: 'ACTIVE', label: 'Ativos' },
  { value: 'INACTIVE', label: 'Arquivados' },
];
const weekdays = [
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
  { value: 0, label: 'Dom' },
];

export type ResourceListSort = 'displayName' | 'createdAt';
const resourceColumns: Array<{ sort: ResourceListSort; label: string }> = [
  { sort: 'displayName', label: 'Nome' },
  { sort: 'createdAt', label: 'Cadastrado em' },
];

/// O que muda entre as duas listas é só o nome das coisas: as duas são recursos da Agenda.
const copy = {
  PROFESSIONAL: {
    one: 'profissional',
    many: 'profissionais',
    loading: 'Carregando profissionais',
    empty: 'Nenhum profissional encontrado',
    emptyHint: 'Cadastre quem atende e quem recebe comissão nesta unidade.',
    created: 'Profissional cadastrado.',
    updated: 'Profissional atualizado.',
    archived: 'Profissional arquivado.',
    restored: 'Profissional reativado.',
    detailColumn: 'Serviços e acesso',
    dialogHint:
      'A disponibilidade semanal é o expediente habitual; fora dele a agenda avisa, sem recusar.',
  },
  ROOM: {
    one: 'sala',
    many: 'salas',
    loading: 'Carregando salas',
    empty: 'Nenhuma sala encontrada',
    emptyHint: 'Cadastre as salas usadas pela agenda.',
    created: 'Sala cadastrada.',
    updated: 'Sala atualizada.',
    archived: 'Sala arquivada.',
    restored: 'Sala reativada.',
    detailColumn: 'Capacidade',
    dialogHint:
      'A disponibilidade semanal é o horário habitual; fora dele a agenda avisa, sem recusar.',
  },
} as const;

/// A lista não sabe de onde vem o estado: a Agenda guarda o dela na aba, e Profissionais no endereço
/// da própria página.
export interface ResourceListView {
  search: string;
  activity: Array<'ACTIVE' | 'INACTIVE'>;
  sort: ResourceListSort;
  direction: 'asc' | 'desc';
  /// O cadastro aberto; vazio é nenhum.
  open: string;
}

/// Lista e cadastro de profissionais ou de salas. Os dois continuam sendo recursos da Agenda
/// (`SchedulingResource`), mas cada um mora onde quem usa procura (ADR 0083).
export function ResourcesPanel({
  kind,
  canManage,
  timeZone,
  view,
  onView,
}: {
  kind: SchedulingResourceKind;
  canManage: boolean;
  timeZone: string;
  view: ResourceListView;
  onView: (patch: Partial<ResourceListView>, options?: { replace?: boolean }) => void;
}) {
  const text = copy[kind];
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const collectionView = useCollectionView();
  const wide = useIsWideViewport() && collectionView === 'list';
  const [createOpen, setCreateOpen] = useState(false);
  const [activationTarget, setActivationTarget] = useState<SchedulingResource>();
  const filters = {
    ...(view.search.trim() ? { search: view.search.trim() } : {}),
    // A consulta conhece um valor ou todos: as duas marcadas é o mesmo que nenhuma.
    activity: view.activity.length === 1 ? view.activity[0]! : ('ALL' as const),
    kind,
    sort: view.sort,
    direction: view.direction,
  };
  const resources = useQuery({
    queryKey: ['company', 'scheduling', 'resources', filters],
    queryFn: () => schedulingApi.listResources(filters),
    retry: false,
  });
  const editing = resources.data?.find((item) => item.id === view.open) ?? null;
  const count = resources.data?.length ?? 0;
  const countLabel = `${count} ${count === 1 ? text.one : text.many}`;
  function toggleSort(column: ResourceListSort) {
    if (view.sort === column) onView({ direction: view.direction === 'asc' ? 'desc' : 'asc' });
    else onView({ sort: column, direction: 'asc' });
  }
  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['company', 'scheduling'] });
  }
  async function activate(resource: SchedulingResource) {
    try {
      await schedulingApi.setResourceActive(resource.id, !resource.active);
      await refresh();
      setActivationTarget(undefined);
      snackbar.success(resource.active ? text.archived : text.restored);
    } catch (cause) {
      snackbar.failure(getApiError(cause));
    }
  }
  const chips = choiceChips('activity', view.activity, activityOptions, (activity) =>
    onView({ activity }),
  );
  return (
    <div>
      <div className="mb-5 flex justify-end">
        {canManage ? (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> Cadastrar {text.one}
          </Button>
        ) : null}
      </div>
      <FilterBar
        searchId="resource-search"
        searchLabel={`Buscar ${text.one}`}
        searchPlaceholder={`Nome ${kind === 'ROOM' ? 'da sala' : 'do profissional'}`}
        search={view.search}
        onSearchChange={(search) => onView({ search }, { replace: true })}
        chips={chips}
        onClear={() => onView({ activity: [] })}
        count={countLabel}
        resultsLabel={`Ver ${countLabel}`}
      >
        <MultiFilter
          label="Situação"
          itemNoun="situações"
          allLabel="Todas as situações"
          options={activityOptions}
          value={view.activity}
          onChange={(activity) => onView({ activity })}
        />
      </FilterBar>
      {resources.isLoading ? (
        <LoadingCard label={text.loading} />
      ) : resources.error ? (
        <ErrorCard message={getApiError(resources.error)} />
      ) : count === 0 ? (
        <EmptyCard
          icon={kind === 'ROOM' ? DoorOpen : UserRound}
          title={text.empty}
          description={text.emptyHint}
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          {wide ? null : (
            <div>
              <SortBar
                label={`Ordenação de ${text.many}`}
                columns={resourceColumns}
                sort={view.sort}
                direction={view.direction}
                onToggle={toggleSort}
              />
              <ul className="divide-y divide-surface-border">
                {resources.data?.map((resource) => (
                  <li key={resource.id} className="flex items-start gap-3 p-4">
                    <ResourceIcon resource={resource} />
                    <div className="min-w-0 flex-1">
                      <strong className="block truncate text-sm">{resource.displayName}</strong>
                      <span className="text-xs text-content-muted">
                        {detailLabel(resource)} · {availabilityLabel(resource)}
                      </span>
                      {resource.member ? (
                        <span className="mt-0.5 block truncate text-xs text-content-faint">
                          Acessa o sistema como {resource.member.displayName}
                        </span>
                      ) : null}
                      {resource.active ? null : (
                        <div className="mt-2">
                          <StatusPill tone="neutral">Arquivado</StatusPill>
                        </div>
                      )}
                    </div>
                    <ResourceActions
                      resource={resource}
                      editLabel={`Editar ${text.one}`}
                      canManage={canManage}
                      onEdit={() => onView({ open: resource.id })}
                      onActivation={() => setActivationTarget(resource)}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
          {wide ? (
            <table className="w-full table-fixed border-collapse text-left">
              <caption className="sr-only">{kind === 'ROOM' ? 'Salas' : 'Profissionais'}</caption>
              <thead>
                <tr className="border-b border-surface-border">
                  <SortableColumn
                    column="displayName"
                    label="Nome"
                    sort={view.sort}
                    direction={view.direction}
                    onToggle={toggleSort}
                  />
                  <th className="px-5 py-3 text-xs">{text.detailColumn}</th>
                  <SortableColumn
                    column="createdAt"
                    label="Cadastrado em"
                    sort={view.sort}
                    direction={view.direction}
                    onToggle={toggleSort}
                  />
                  <th className="w-64 px-5 py-3 text-xs">Disponibilidade</th>
                  <th className="w-24">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {resources.data?.map((resource) => (
                  <tr key={resource.id}>
                    <td className="px-5 py-4">
                      <strong className="block truncate text-sm">{resource.displayName}</strong>
                      {resource.active ? null : <StatusPill tone="neutral">Arquivado</StatusPill>}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {detailLabel(resource)}
                      {resource.member ? (
                        <span className="mt-0.5 block truncate text-content-faint">
                          {resource.member.displayName}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {formatDateTime(resource.createdAt, timeZone)}
                    </td>
                    <td className="px-5 py-4 text-xs text-content-muted">
                      {availabilityLabel(resource)}
                    </td>
                    <td className="px-3 py-4">
                      <ResourceActions
                        resource={resource}
                        editLabel={`Editar ${text.one}`}
                        canManage={canManage}
                        onEdit={() => onView({ open: resource.id })}
                        onActivation={() => setActivationTarget(resource)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      )}
      <ResourceDialog
        kind={kind}
        open={createOpen}
        onOpenChange={setCreateOpen}
        resource={null}
        onSaved={async () => {
          await refresh();
          setCreateOpen(false);
          snackbar.success(text.created);
        }}
      />
      <ResourceDialog
        kind={kind}
        open={Boolean(view.open)}
        onOpenChange={(open) => {
          if (!open) onView({ open: '' });
        }}
        resource={editing}
        readOnly={!canManage}
        onSaved={async () => {
          await refresh();
          onView({ open: '' });
          snackbar.success(text.updated);
        }}
      />
      {activationTarget ? (
        <Modal
          open
          onOpenChange={() => setActivationTarget(undefined)}
          title={`${activationTarget.active ? 'Arquivar' : 'Reativar'} ${text.one}`}
          description="Agendamentos existentes permanecem preservados."
        >
          <p className="text-sm">
            {activationTarget.active ? 'Arquivar' : 'Reativar'}{' '}
            <strong>{activationTarget.displayName}</strong>?
          </p>
          <DialogActions
            label={`${activationTarget.active ? 'Arquivar' : 'Reativar'} ${text.one}`}
            onCancel={() => setActivationTarget(undefined)}
            onSubmit={() => void activate(activationTarget)}
          />
        </Modal>
      ) : null}
    </div>
  );
}

function ResourceIcon({ resource }: { resource: SchedulingResource }) {
  return (
    <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-400">
      {resource.kind === 'PROFESSIONAL' ? (
        <UserRound className="size-5" />
      ) : (
        <DoorOpen className="size-5" />
      )}
    </span>
  );
}
function ResourceActions({
  resource,
  editLabel,
  canManage,
  onEdit,
  onActivation,
}: {
  resource: SchedulingResource;
  editLabel: string;
  canManage: boolean;
  onEdit: () => void;
  onActivation: () => void;
}) {
  return canManage ? (
    <div className="flex justify-end gap-1">
      <IconButton label={editLabel} icon={<PencilLine className="size-4" />} onClick={onEdit} />
      <IconButton
        label={resource.active ? 'Arquivar' : 'Reativar'}
        {...(resource.active ? { tone: 'danger' as const } : {})}
        icon={resource.active ? <Archive className="size-4" /> : <RotateCcw className="size-4" />}
        onClick={onActivation}
      />
    </div>
  ) : null;
}

const resourceFormSchema = z
  .object({
    kind: z.enum(['PROFESSIONAL', 'ROOM']),
    displayName: z.string().trim().min(2, 'Informe o nome.').max(160),
    /// Vazio é o caso comum: profissional é cadastro operacional e existe sem acesso ao sistema.
    membershipId: z.string(),
    /// Só a sala mostra e usa o campo; no profissional ele fica fora da conta e vai 1.
    capacity: z.string(),
    /// Serviços que o profissional realiza (ADR 0074); a sala não usa.
    serviceIds: z.array(z.string()),
    /// Só a ficha com a feature de comissões mostra e envia estes campos (ADR 0082).
    receivesCommission: z.boolean(),
    attendsSchedule: z.boolean(),
    employmentBond: z.enum([...EMPLOYMENT_BONDS, '']),
    commissionOffPayroll: z.boolean(),
    /// Preço e duração próprios em alguns desses serviços (ADR 0074, decisão 8).
    serviceTerms: z.array(
      z.object({
        serviceId: z.string(),
        priceCents: z.number().int().min(0).nullable(),
        durationMinutes: z
          .number()
          .int('Informe a duração em minutos inteiros.')
          .min(5, 'A duração do profissional vai de 5 minutos a 24 horas.')
          .max(1440, 'A duração do profissional vai de 5 minutos a 24 horas.')
          .nullable(),
      }),
    ),
    weekdays: z.array(z.number()).min(1, 'Escolha ao menos um dia.'),
    startTime: z.string().min(1, 'Informe o início.'),
    endTime: z.string().min(1, 'Informe o fim.'),
  })
  .refine((value) => value.startTime < value.endTime, {
    path: ['endTime'],
    message: 'O fim deve ser posterior ao início.',
  })
  .refine((value) => !value.receivesCommission || value.employmentBond !== '', {
    path: ['employmentBond'],
    message: 'Informe o vínculo de trabalho de quem recebe comissão.',
  })
  .refine(
    (value) =>
      value.kind !== 'ROOM' ||
      (/^\d+$/u.test(value.capacity) &&
        Number(value.capacity) >= 1 &&
        Number(value.capacity) <= 50),
    { path: ['capacity'], message: 'Use um número de 1 a 50.' },
  );
type ResourceForm = z.infer<typeof resourceFormSchema>;
function ResourceDialog({
  kind,
  open,
  onOpenChange,
  resource,
  readOnly = false,
  onSaved,
}: {
  kind: SchedulingResourceKind;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resource: SchedulingResource | null;
  readOnly?: boolean;
  onSaved: () => Promise<void>;
}) {
  const text = copy[kind];
  const {
    activeBusinessUnitId,
    commissionsEnabled,
    canRegisterAdvances,
    today: advanceToday = '',
  } = useSchedulingUnits();
  const [requestError, setRequestError] = useState<string>();
  const form = useForm<ResourceForm>({
    resolver: zodResolver(resourceFormSchema),
    defaultValues: emptyResource(kind),
  });
  const values = useWatch({ control: form.control }) as ResourceForm;
  /// Quem pode ser vinculado. A consulta é da agenda, não de Pessoas e acessos: quem cadastra
  /// recurso escolhe a pessoa sem administrar acessos. Só quem edita precisa dela.
  const members = useQuery({
    queryKey: ['company', 'scheduling', 'members'],
    queryFn: schedulingApi.members,
    enabled: open && !readOnly,
    retry: false,
  });
  /// Arquivados também entram: a ligação continua valendo para quando o serviço voltar.
  const services = useQuery({
    queryKey: ['company', 'scheduling', 'resource-service-options'],
    queryFn: async () => {
      const found: ServiceOffering[] = [];
      let total = 0;
      do {
        const page = await servicesApi.list({
          activity: 'ALL',
          currency: 'ALL',
          sort: 'displayName',
          direction: 'asc',
          limit: 100,
          offset: found.length,
        });
        total = page.total;
        found.push(...page.services);
        if (!page.services.length) break;
      } while (found.length < total);
      return found;
    },
    enabled: open && values.kind === 'PROFESSIONAL',
    retry: false,
  });
  const serviceOptions = (services.data ?? []).map((service) => ({
    value: service.id,
    label: service.active ? service.displayName : `${service.displayName} (arquivado)`,
  }));
  useEffect(() => {
    if (open) {
      form.reset(resource ? resourceFormOf(resource) : emptyResource(kind));
    }
  }, [form, kind, open, resource]);
  function changeOpen(nextOpen: boolean) {
    if (!nextOpen) setRequestError(undefined);
    onOpenChange(nextOpen);
  }
  async function save(data: ResourceForm) {
    setRequestError(undefined);
    // Sala não tem gente, e vazio significa "sem acesso": nos dois casos vai nulo, e não string.
    const { receivesCommission, attendsSchedule, employmentBond, commissionOffPayroll, ...fields } =
      data;
    const input = {
      ...fields,
      ...(commissionsEnabled && data.kind === 'PROFESSIONAL'
        ? {
            commission: {
              receivesCommission,
              attendsSchedule,
              employmentBond: employmentBond === '' ? null : employmentBond,
              commissionOffPayroll,
            },
          }
        : {}),
      serviceIds: data.kind === 'PROFESSIONAL' ? data.serviceIds : [],
      serviceTerms:
        data.kind === 'PROFESSIONAL'
          ? data.serviceTerms
              .filter(
                (terms) =>
                  data.serviceIds.includes(terms.serviceId) &&
                  (terms.priceCents !== null || terms.durationMinutes !== null),
              )
              .sort((left, right) => left.serviceId.localeCompare(right.serviceId))
          : [],
      membershipId: data.kind === 'PROFESSIONAL' && data.membershipId ? data.membershipId : null,
      // Profissional atende um por vez; só a sala comporta mais (ADR 0035).
      capacity: data.kind === 'ROOM' ? Number(data.capacity) : 1,
    };
    try {
      if (resource) await schedulingApi.updateResource(resource.id, input);
      else await schedulingApi.createResource(input);
      await onSaved();
    } catch (cause) {
      setRequestError(getApiError(cause));
    }
  }
  function toggleWeekday(day: number) {
    const current = values.weekdays;
    form.setValue(
      'weekdays',
      current.includes(day) ? current.filter((one) => one !== day) : [...current, day],
      { shouldValidate: true },
    );
  }
  return (
    <Modal
      open={open}
      onOpenChange={changeOpen}
      title={`${resource ? 'Editar' : 'Cadastrar'} ${text.one}`}
      description={text.dialogHint}
    >
      <form noValidate onSubmit={(event) => void form.handleSubmit(save)(event)}>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Input
              label={kind === 'ROOM' ? 'Nome da sala' : 'Nome do profissional'}
              error={form.formState.errors.displayName?.message}
              disabled={readOnly}
              {...form.register('displayName')}
            />
          </div>
          {values.kind === 'PROFESSIONAL' ? (
            readOnly ? (
              <Input
                label="Pessoa com acesso"
                value={resource?.member?.displayName ?? 'Sem acesso ao sistema'}
                readOnly
                disabled
              />
            ) : (
              <Select
                label="Pessoa com acesso"
                value={values.membershipId}
                options={[
                  { value: '', label: 'Sem acesso ao sistema' },
                  ...(members.data ?? []).map((member) => ({
                    value: member.membershipId,
                    label: member.displayName,
                  })),
                ]}
                onValueChange={(value) => form.setValue('membershipId', value)}
              />
            )
          ) : null}
          {values.kind === 'PROFESSIONAL' ? (
            <div className="sm:col-span-2">
              {readOnly ? (
                <Input
                  label="Serviços que realiza"
                  value={
                    serviceOptions
                      .filter((option) => values.serviceIds.includes(option.value))
                      .map((option) => option.label)
                      .join(', ') || 'Nenhum serviço'
                  }
                  readOnly
                  disabled
                />
              ) : (
                <MultiSelect
                  label="Serviços que realiza"
                  itemNoun="serviços"
                  allLabel="Todos os serviços"
                  noneLabel="Nenhum serviço"
                  searchPlaceholder="Buscar serviço"
                  options={serviceOptions}
                  selected={values.serviceIds}
                  onChange={(next) => form.setValue('serviceIds', next)}
                />
              )}
              <p className="mt-1.5 text-xs text-content-faint">
                {services.isError
                  ? 'Não foi possível carregar os serviços. Feche e abra de novo.'
                  : values.serviceIds.length === 0
                    ? 'Sem serviço marcado, o profissional não aparece para agendar.'
                    : 'O profissional só é oferecido, aqui e no portal, para os serviços marcados.'}
              </p>
            </div>
          ) : null}
          {commissionsEnabled && values.kind === 'PROFESSIONAL' ? (
            <CommissionProfileFields
              value={{
                receivesCommission: values.receivesCommission,
                attendsSchedule: values.attendsSchedule,
                employmentBond: values.employmentBond,
                commissionOffPayroll: values.commissionOffPayroll,
              }}
              onChange={(next) => {
                form.setValue('receivesCommission', next.receivesCommission);
                form.setValue('attendsSchedule', next.attendsSchedule);
                form.setValue('employmentBond', next.employmentBond, {
                  shouldValidate: form.formState.isSubmitted,
                });
                form.setValue('commissionOffPayroll', next.commissionOffPayroll);
              }}
              readOnly={readOnly}
              error={form.formState.errors.employmentBond?.message}
              advances={
                canRegisterAdvances && resource && values.receivesCommission && !readOnly ? (
                  <ProfessionalAdvances resource={resource} today={advanceToday} />
                ) : null
              }
            />
          ) : null}
          {values.kind === 'PROFESSIONAL' ? (
            <ProfessionalServiceTermsFields
              services={(services.data ?? []).filter((service) =>
                values.serviceIds.includes(service.id),
              )}
              businessUnitId={resource?.businessUnitId ?? activeBusinessUnitId}
              value={values.serviceTerms}
              onChange={(next) => form.setValue('serviceTerms', next)}
              disabled={readOnly}
            />
          ) : (
            <Input
              label="Atendimentos ao mesmo tempo"
              inputMode="numeric"
              hint="Cadeiras ou macas: quantos clientes a sala recebe juntos."
              error={form.formState.errors.capacity?.message}
              disabled={readOnly}
              {...form.register('capacity')}
            />
          )}
          <div className="sm:col-span-2">
            <div className="mb-2 flex items-center justify-between gap-3">
              <span className="block text-xs font-semibold text-content/70">Dias disponíveis</span>
              {readOnly ? null : (
                <SelectAllToggle
                  scope="dias disponíveis"
                  total={weekdays.length}
                  selected={values.weekdays.length}
                  onSelectAll={() =>
                    form.setValue(
                      'weekdays',
                      weekdays.map((day) => day.value),
                      { shouldValidate: true },
                    )
                  }
                  onClear={() => form.setValue('weekdays', [], { shouldValidate: true })}
                />
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {weekdays.map((day) => (
                <button
                  key={day.value}
                  type="button"
                  aria-pressed={values.weekdays.includes(day.value)}
                  onClick={() => toggleWeekday(day.value)}
                  disabled={readOnly}
                  className={`min-h-10 cursor-pointer rounded-xl border px-3 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${values.weekdays.includes(day.value) ? 'border-brand-500 bg-brand-500/15 text-brand-400' : 'border-surface-border text-content-muted'}`}
                >
                  {day.label}
                </button>
              ))}
            </div>
            <FieldError message={form.formState.errors.weekdays?.message} />
          </div>
          <TimePicker
            label="Início"
            value={values.startTime || null}
            onValueChange={(value) =>
              form.setValue('startTime', value ?? '', { shouldValidate: true })
            }
            error={form.formState.errors.startTime?.message}
            disabled={readOnly}
          />
          <TimePicker
            label="Fim"
            value={values.endTime || null}
            onValueChange={(value) =>
              form.setValue('endTime', value ?? '', { shouldValidate: true })
            }
            error={form.formState.errors.endTime?.message}
            disabled={readOnly}
          />
        </div>
        {requestError ? <RequestAlert message={requestError} /> : null}
        {readOnly ? null : (
          <DialogActions
            label={`${resource ? 'Salvar' : 'Cadastrar'} ${text.one}`}
            onCancel={() => changeOpen(false)}
            loading={form.formState.isSubmitting}
          />
        )}
      </form>
    </Modal>
  );
}

function emptyResource(kind: SchedulingResourceKind): ResourceForm {
  return {
    kind,
    displayName: '',
    membershipId: '',
    capacity: '1',
    serviceIds: [],
    receivesCommission: false,
    attendsSchedule: true,
    employmentBond: '',
    commissionOffPayroll: false,
    serviceTerms: [],
    weekdays: [1, 2, 3, 4, 5],
    startTime: '09:00',
    endTime: '18:00',
  };
}
function resourceFormOf(resource: SchedulingResource): ResourceForm {
  const first = resource.availability[0];
  return {
    kind: resource.kind,
    displayName: resource.displayName,
    membershipId: resource.member?.membershipId ?? '',
    capacity: String(resource.capacity),
    serviceIds: resource.serviceIds,
    receivesCommission: resource.receivesCommission ?? false,
    attendsSchedule: resource.attendsSchedule ?? true,
    employmentBond: resource.employmentBond ?? '',
    commissionOffPayroll: resource.commissionOffPayroll ?? false,
    serviceTerms: resource.serviceTerms ?? [],
    weekdays: [...new Set(resource.availability.map((window) => window.weekday))],
    startTime: minuteLabel(first?.startMinute ?? 540),
    endTime: minuteLabel(first?.endMinute ?? 1080),
  };
}
/// A sala que recebe vários clientes juntos diz quantos: é o que decide se cabe mais um.
function availabilityLabel(resource: SchedulingResource) {
  const first = resource.availability[0];
  const days = weekdays
    .filter((day) => resource.availability.some((window) => window.weekday === day.value))
    .map((day) => day.label)
    .join(', ');
  return first
    ? `${days} · ${minuteLabel(first.startMinute)}–${minuteLabel(first.endMinute)}`
    : 'Sem disponibilidade';
}
